// Tenant roles
export const TENANT_ROLE = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  HR_ADMIN: 'HR_ADMIN',
  HR_MANAGER: 'HR_MANAGER',
  PAYROLL_ADMIN: 'PAYROLL_ADMIN',
  BENEFITS_ADMIN: 'BENEFITS_ADMIN',
  RECRUITER: 'RECRUITER',
  MANAGER: 'MANAGER',
  EMPLOYEE: 'EMPLOYEE',
} as const;

export const TENANT_ROLE_LABELS: Record<keyof typeof TENANT_ROLE, string> = {
  SUPER_ADMIN: 'Super Admin',
  HR_ADMIN: 'HR Administrator',
  HR_MANAGER: 'HR Manager',
  PAYROLL_ADMIN: 'Payroll Administrator',
  BENEFITS_ADMIN: 'Benefits Administrator',
  RECRUITER: 'Recruiter',
  MANAGER: 'Manager',
  EMPLOYEE: 'Employee',
};

// Role hierarchy (higher number = more permissions)
export const ROLE_HIERARCHY: Record<keyof typeof TENANT_ROLE, number> = {
  SUPER_ADMIN: 100,
  HR_ADMIN: 80,
  PAYROLL_ADMIN: 70,
  BENEFITS_ADMIN: 70,
  HR_MANAGER: 60,
  RECRUITER: 50,
  MANAGER: 40,
  EMPLOYEE: 10,
};

// Permission categories
export const PERMISSION_CATEGORY = {
  EMPLOYEES: 'employees',
  DEPARTMENTS: 'departments',
  LOCATIONS: 'locations',
  JOBS: 'jobs',
  COMPENSATION: 'compensation',
  PAYROLL: 'payroll',
  BENEFITS: 'benefits',
  RECRUITING: 'recruiting',
  PERFORMANCE: 'performance',
  LEARNING: 'learning',
  REPORTS: 'reports',
  SETTINGS: 'settings',
  AUDIT: 'audit',
} as const;

// Permissions
export const PERMISSION = {
  // Employees
  EMPLOYEES_READ: 'employees:read',
  EMPLOYEES_WRITE: 'employees:write',
  EMPLOYEES_DELETE: 'employees:delete',
  EMPLOYEES_SENSITIVE: 'employees:sensitive', // SSN, salary, etc.

  // Departments
  DEPARTMENTS_READ: 'departments:read',
  DEPARTMENTS_WRITE: 'departments:write',
  DEPARTMENTS_DELETE: 'departments:delete',

  // Locations
  LOCATIONS_READ: 'locations:read',
  LOCATIONS_WRITE: 'locations:write',
  LOCATIONS_DELETE: 'locations:delete',

  // Jobs
  JOBS_READ: 'jobs:read',
  JOBS_WRITE: 'jobs:write',
  JOBS_DELETE: 'jobs:delete',

  // Compensation
  COMPENSATION_READ: 'compensation:read',
  COMPENSATION_WRITE: 'compensation:write',

  // Payroll
  PAYROLL_READ: 'payroll:read',
  PAYROLL_WRITE: 'payroll:write',
  PAYROLL_RUN: 'payroll:run',

  // Benefits
  BENEFITS_READ: 'benefits:read',
  BENEFITS_WRITE: 'benefits:write',
  BENEFITS_ENROLL: 'benefits:enroll',

  // Recruiting
  RECRUITING_READ: 'recruiting:read',
  RECRUITING_WRITE: 'recruiting:write',

  // Performance
  PERFORMANCE_READ: 'performance:read',
  PERFORMANCE_WRITE: 'performance:write',

  // Learning
  LEARNING_READ: 'learning:read',
  LEARNING_WRITE: 'learning:write',

  // Reports
  REPORTS_READ: 'reports:read',
  REPORTS_EXPORT: 'reports:export',

  // Settings
  SETTINGS_READ: 'settings:read',
  SETTINGS_WRITE: 'settings:write',

  // Audit
  AUDIT_READ: 'audit:read',
} as const;

// Role permissions mapping
export const ROLE_PERMISSIONS: Record<keyof typeof TENANT_ROLE, string[]> = {
  SUPER_ADMIN: Object.values(PERMISSION),
  HR_ADMIN: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.EMPLOYEES_WRITE,
    PERMISSION.EMPLOYEES_DELETE,
    PERMISSION.EMPLOYEES_SENSITIVE,
    PERMISSION.DEPARTMENTS_READ,
    PERMISSION.DEPARTMENTS_WRITE,
    PERMISSION.DEPARTMENTS_DELETE,
    PERMISSION.LOCATIONS_READ,
    PERMISSION.LOCATIONS_WRITE,
    PERMISSION.LOCATIONS_DELETE,
    PERMISSION.JOBS_READ,
    PERMISSION.JOBS_WRITE,
    PERMISSION.JOBS_DELETE,
    PERMISSION.COMPENSATION_READ,
    PERMISSION.COMPENSATION_WRITE,
    PERMISSION.BENEFITS_READ,
    PERMISSION.BENEFITS_WRITE,
    PERMISSION.RECRUITING_READ,
    PERMISSION.RECRUITING_WRITE,
    PERMISSION.PERFORMANCE_READ,
    PERMISSION.PERFORMANCE_WRITE,
    PERMISSION.LEARNING_READ,
    PERMISSION.LEARNING_WRITE,
    PERMISSION.REPORTS_READ,
    PERMISSION.REPORTS_EXPORT,
    PERMISSION.SETTINGS_READ,
    PERMISSION.AUDIT_READ,
  ],
  HR_MANAGER: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.EMPLOYEES_WRITE,
    PERMISSION.DEPARTMENTS_READ,
    PERMISSION.LOCATIONS_READ,
    PERMISSION.JOBS_READ,
    PERMISSION.COMPENSATION_READ,
    PERMISSION.BENEFITS_READ,
    PERMISSION.RECRUITING_READ,
    PERMISSION.RECRUITING_WRITE,
    PERMISSION.PERFORMANCE_READ,
    PERMISSION.PERFORMANCE_WRITE,
    PERMISSION.LEARNING_READ,
    PERMISSION.LEARNING_WRITE,
    PERMISSION.REPORTS_READ,
  ],
  PAYROLL_ADMIN: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.EMPLOYEES_SENSITIVE,
    PERMISSION.COMPENSATION_READ,
    PERMISSION.COMPENSATION_WRITE,
    PERMISSION.PAYROLL_READ,
    PERMISSION.PAYROLL_WRITE,
    PERMISSION.PAYROLL_RUN,
    PERMISSION.REPORTS_READ,
    PERMISSION.REPORTS_EXPORT,
  ],
  BENEFITS_ADMIN: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.BENEFITS_READ,
    PERMISSION.BENEFITS_WRITE,
    PERMISSION.BENEFITS_ENROLL,
    PERMISSION.REPORTS_READ,
  ],
  RECRUITER: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.DEPARTMENTS_READ,
    PERMISSION.LOCATIONS_READ,
    PERMISSION.JOBS_READ,
    PERMISSION.RECRUITING_READ,
    PERMISSION.RECRUITING_WRITE,
  ],
  MANAGER: [
    PERMISSION.EMPLOYEES_READ,
    PERMISSION.DEPARTMENTS_READ,
    PERMISSION.LOCATIONS_READ,
    PERMISSION.JOBS_READ,
    PERMISSION.PERFORMANCE_READ,
    PERMISSION.PERFORMANCE_WRITE,
    PERMISSION.LEARNING_READ,
    PERMISSION.REPORTS_READ,
  ],
  EMPLOYEE: [
    PERMISSION.EMPLOYEES_READ, // Own profile only (enforced at service level)
    PERMISSION.DEPARTMENTS_READ,
    PERMISSION.LOCATIONS_READ,
    PERMISSION.LEARNING_READ,
    PERMISSION.BENEFITS_READ,
    PERMISSION.BENEFITS_ENROLL, // Own enrollment only
  ],
};
